import { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Snackbar,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import DateRangeOutlinedIcon from "@mui/icons-material/DateRangeOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ReportGmailerrorredOutlinedIcon from "@mui/icons-material/ReportGmailerrorredOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import AddIcon from "@mui/icons-material/Add";
import dayjs from "dayjs";
import TinymceEditor from "../../components/TinymceEditor";
import { EMAIL_EDITOR_INIT } from "./editorConfig";
import {
  listEmailTemplates,
  previewEmailTemplate,
  resetEmailTemplate,
  saveEmailTemplate,
} from "../../distflowAPI/emailTemplatesApi";

const INK = "#1E2230";
const MUTED = "#6E7385";
const LINE = "#E4E5E7";
const ACCENT = "#6576FF";
const UNSAVED = "#C77700";

// Fields every email has, about the account it goes to (emailqueue/email_templates.py).
const ACCOUNT_FIELDS = [
  "customer_name",
  "customer_company",
  "account_number",
  "account_address",
  "salesman_name",
  "salesman_phone",
  "today",
];

const GROUPS = [
  { title: "Orders", kinds: ["order_confirmation", "order_delivered"] },
  {
    title: "Invoices and billing",
    kinds: ["invoice", "weekly_invoices", "statement", "overdue_reminder"],
  },
];

// Per email: its icon, and what the system adds to it when it is sent.
const EMAILS = {
  order_confirmation: {
    icon: ShoppingCartOutlinedIcon,
    added: ["Order table", "Order PDF"],
  },
  order_delivered: {
    icon: LocalShippingOutlinedIcon,
    added: ["Delivered items table", "Order PDF", "Delivery photos"],
  },
  invoice: {
    icon: ReceiptLongOutlinedIcon,
    added: ["Invoice table", "Invoice PDF"],
  },
  weekly_invoices: {
    icon: DateRangeOutlinedIcon,
    added: ["The week's invoices (PDF)"],
  },
  statement: { icon: DescriptionOutlinedIcon, added: ["Statement PDF"] },
  overdue_reminder: {
    icon: ReportGmailerrorredOutlinedIcon,
    added: ["Account summary statement (PDF)"],
  },
};

function editedText(t) {
  if (!t.customized) return "Original wording";
  const when = dayjs(t.updated_at).format("MMM D");
  return t.updated_by ? `Edited ${when} by ${t.updated_by}` : `Edited ${when}`;
}

// Wording of the emails sent to customers.
export default function EmailTemplates() {
  const [templates, setTemplates] = useState(null);
  const [selectedKind, setSelectedKind] = useState(null);
  const [error, setError] = useState("");
  const dirty = useRef(false);

  useEffect(() => {
    listEmailTemplates()
      .then((res) => {
        setTemplates(res.data);
        setSelectedKind(res.data[0]?.kind);
      })
      .catch((err) =>
        setError(
          err?.response?.status === 403
            ? "Only office staff can change email templates."
            : "Could not load the email templates."
        )
      );
  }, []);

  useEffect(() => {
    const warn = (e) => {
      if (dirty.current) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, []);

  function select(kind) {
    if (kind === selectedKind) return;
    if (
      dirty.current &&
      !window.confirm("You have unsaved changes. Leave them and switch emails?")
    ) {
      return;
    }
    dirty.current = false;
    setSelectedKind(kind);
  }

  function replaceTemplate(updated) {
    setTemplates((current) =>
      current.map((t) => (t.kind === updated.kind ? updated : t))
    );
  }

  const selected = templates?.find((t) => t.kind === selectedKind);
  const grouped = templates
    ? [
        ...GROUPS.map((g) => ({
          ...g,
          items: templates.filter((t) => g.kinds.includes(t.kind)),
        })),
        {
          title: "Other",
          items: templates.filter(
            (t) => !GROUPS.some((g) => g.kinds.includes(t.kind))
          ),
        },
      ].filter((g) => g.items.length)
    : [];

  return (
    <Box sx={{ color: INK }}>
      <Typography sx={{ fontSize: 23, fontWeight: 600 }}>
        Email templates
      </Typography>
      <Typography
        sx={{ color: MUTED, fontSize: 14, mt: 0.5, mb: 3, maxWidth: 640 }}
      >
        What customers read in the emails we send them. Fields in curly braces,
        like {"{customer_name}"}, are filled in for each customer.
      </Typography>
      {error && <Alert severity="error">{error}</Alert>}
      {!templates && !error && <CircularProgress size={24} />}
      {templates && (
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={{ xs: 2, md: 4 }}
          alignItems="flex-start"
        >
          <Box
            component="nav"
            aria-label="Emails"
            sx={{ width: { xs: "100%", md: 250 }, flexShrink: 0 }}
          >
            {grouped.map((group) => (
              <Box key={group.title} mb={2.5}>
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: MUTED,
                    mb: 0.5,
                    pl: 1.5,
                  }}
                >
                  {group.title}
                </Typography>
                {group.items.map((t) => (
                  <EmailListItem
                    key={t.kind}
                    template={t}
                    selected={t.kind === selectedKind}
                    onClick={() => select(t.kind)}
                  />
                ))}
              </Box>
            ))}
          </Box>
          {selected && (
            <Box flexGrow={1} minWidth={0} width="100%">
              <TemplateEditor
                key={selected.kind}
                template={selected}
                onSaved={replaceTemplate}
                onDirtyChange={(value) => (dirty.current = value)}
              />
            </Box>
          )}
        </Stack>
      )}
    </Box>
  );
}

function EmailListItem({ template, selected, onClick }) {
  const Icon = EMAILS[template.kind]?.icon || MailOutlineIcon;
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      aria-current={selected ? "true" : undefined}
      sx={{
        all: "unset",
        boxSizing: "border-box",
        display: "flex",
        gap: 1.5,
        width: "100%",
        cursor: "pointer",
        py: 1.25,
        pl: 1.5,
        pr: 1,
        borderLeft: 3,
        borderColor: selected ? ACCENT : "transparent",
        bgcolor: selected ? "#fff" : "transparent",
        borderRadius: "0 6px 6px 0",
        "&:hover": { bgcolor: selected ? "#fff" : "rgba(30,34,48,0.04)" },
        "&:focus-visible": {
          outline: `2px solid ${ACCENT}`,
          outlineOffset: -2,
        },
      }}
    >
      <Icon
        sx={{ fontSize: 20, mt: "2px", color: selected ? ACCENT : MUTED }}
      />
      <Box>
        <Typography
          sx={{ fontSize: 14, fontWeight: selected ? 600 : 500, color: INK }}
        >
          {template.label}
        </Typography>
        <Typography
          sx={{ fontSize: 12, color: template.customized ? ACCENT : MUTED }}
        >
          {editedText(template)}
        </Typography>
      </Box>
    </Box>
  );
}

function TemplateEditor({ template, onSaved, onDirtyChange }) {
  const [subject, setSubject] = useState(template.subject);
  const [body, setBody] = useState(template.body);
  // The saved message as the editor writes it. The editor rewrites HTML as it
  // loads it (wrapping lines in <p>, for example), so the saved text from the
  // server can't be compared with the editor's text directly.
  const [savedBody, setSavedBody] = useState(template.body);
  const loadingBody = useRef(true);
  const [mode, setMode] = useState("write");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");
  const [fieldsAnchor, setFieldsAnchor] = useState(null);
  const editorRef = useRef(null);
  const subjectRef = useRef(null);
  const lastFocus = useRef("body");
  const subjectCursor = useRef(null);

  const changed = subject !== template.subject || body !== savedBody;

  // Takes whatever the editor made of the text just loaded as the saved version.
  function syncSavedBody() {
    setTimeout(() => {
      const html = editorRef.current?.getContent();
      // Only before the user starts editing: never take their edits as saved.
      if (loadingBody.current && html !== undefined) {
        setBody(html);
        setSavedBody(html);
      }
      loadingBody.current = false;
    });
  }

  function loadBody(html) {
    loadingBody.current = true;
    setBody(html);
    setSavedBody(html);
    syncSavedBody();
  }
  useEffect(() => {
    onDirtyChange(changed);
  }, [changed]); // eslint-disable-line react-hooks/exhaustive-deps

  function rememberSubjectCursor(e) {
    lastFocus.current = "subject";
    subjectCursor.current = [e.target.selectionStart, e.target.selectionEnd];
  }

  function insertField(name) {
    const text = `{${name}}`;
    setFieldsAnchor(null);
    if (lastFocus.current === "subject") {
      const [start, end] = subjectCursor.current || [
        subject.length,
        subject.length,
      ];
      setSubject(subject.slice(0, start) + text + subject.slice(end));
      const cursor = start + text.length;
      subjectCursor.current = [cursor, cursor];
      setTimeout(() => {
        subjectRef.current?.focus();
        subjectRef.current?.setSelectionRange(cursor, cursor);
      });
    } else {
      loadingBody.current = false;
      setMode("write");
      editorRef.current?.focus();
      editorRef.current?.insertContent(text);
    }
  }

  function run(promise, doneText) {
    setSaving(true);
    setError("");
    promise
      .then((res) => {
        onSaved(res.data);
        setSubject(res.data.subject);
        loadBody(res.data.body);
        setToast(doneText);
      })
      .catch((err) =>
        setError(err?.response?.data?.error || "Could not save the template.")
      )
      .finally(() => setSaving(false));
  }

  function handleReset() {
    if (
      window.confirm(
        `Go back to the original wording for "${template.label}"? Your edits will be lost.`
      )
    ) {
      run(resetEmailTemplate(template.kind), "Back to the original wording");
    }
  }

  const added = EMAILS[template.kind]?.added || [];

  return (
    <Box>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ sm: "flex-end" }}
        spacing={1}
        mb={1.5}
      >
        <Box maxWidth={620}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Typography sx={{ fontSize: 19, fontWeight: 600 }}>
              {template.label}
            </Typography>
            {changed && (
              <Typography
                sx={{ fontSize: 12, fontWeight: 600, color: UNSAVED }}
              >
                Unsaved changes
              </Typography>
            )}
          </Stack>
          <Typography sx={{ fontSize: 13, color: MUTED, mt: 0.25 }}>
            {template.description}
          </Typography>
        </Box>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={mode}
          onChange={(e, value) => value && setMode(value)}
          sx={{ bgcolor: "#fff", flexShrink: 0 }}
        >
          <ToggleButton value="write" sx={{ textTransform: "none", px: 2 }}>
            Write
          </ToggleButton>
          <ToggleButton value="preview" sx={{ textTransform: "none", px: 2 }}>
            Preview
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 1.5 }}>
          {error}
        </Alert>
      )}

      {/* The email itself: header lines, message, and what is attached automatically. */}
      <Box
        sx={{
          bgcolor: "#fff",
          border: 1,
          borderColor: LINE,
          borderRadius: "8px",
          overflow: "hidden",
        }}
      >
        <HeaderLine label="To">
          <Typography sx={{ fontSize: 14, color: MUTED }}>
            The account's email addresses
          </Typography>
        </HeaderLine>
        {mode === "write" ? (
          <HeaderLine label="Subject" htmlFor="email-template-subject">
            <Box
              component="input"
              id="email-template-subject"
              aria-label="Subject"
              ref={subjectRef}
              value={subject}
              onChange={(e) => {
                setSubject(e.target.value);
                rememberSubjectCursor(e);
              }}
              onFocus={rememberSubjectCursor}
              onSelect={rememberSubjectCursor}
              onKeyUp={rememberSubjectCursor}
              sx={{
                all: "unset",
                width: "100%",
                fontSize: 15,
                fontWeight: 600,
                color: INK,
                fontFamily: "inherit",
              }}
            />
            <Button
              size="small"
              startIcon={<AddIcon />}
              onMouseDown={(e) => e.preventDefault()}
              onClick={(e) => setFieldsAnchor(e.currentTarget)}
              sx={{ textTransform: "none", flexShrink: 0, color: ACCENT }}
            >
              Insert field
            </Button>
          </HeaderLine>
        ) : null}

        <Box
          display={mode === "write" ? "block" : "none"}
          onFocusCapture={(e) => {
            if (e.target.id !== "email-template-subject")
              lastFocus.current = "body";
          }}
          sx={{
            "& .tox-tinymce": { border: 0, borderRadius: 0 },
            "& .tox .tox-toolbar__primary": {
              borderBottom: `1px solid ${LINE}`,
            },
          }}
        >
          <TinymceEditor
            value={body}
            onEditorChange={(html) => {
              setBody(html);
              if (loadingBody.current) setSavedBody(html);
            }}
            height={440}
            init={EMAIL_EDITOR_INIT}
            onInit={(editor) => {
              editorRef.current = editor;
              editor.on("focus", () => (lastFocus.current = "body"));
              editor.on("keydown paste cut drop", () => {
                loadingBody.current = false;
              });
              syncSavedBody();
            }}
          />
        </Box>
        {mode === "preview" && (
          <EmailPreview kind={template.kind} subject={subject} body={body} />
        )}

        {added.length > 0 && (
          <Box
            sx={{
              borderTop: 1,
              borderColor: LINE,
              px: 2.5,
              py: 1.5,
              bgcolor: "#FAFAFB",
            }}
          >
            <Typography sx={{ fontSize: 12, color: MUTED, mb: 0.75 }}>
              Added automatically when the email is sent
            </Typography>
            <Stack direction="row" gap={1} flexWrap="wrap">
              {added.map((item) => (
                <Chip
                  key={item}
                  icon={<AttachFileIcon />}
                  label={item}
                  size="small"
                  variant="outlined"
                  sx={{ bgcolor: "#fff", borderColor: LINE, fontSize: 12 }}
                />
              ))}
            </Stack>
          </Box>
        )}
      </Box>

      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        mt={2}
        flexWrap="wrap"
        useFlexGap
      >
        {template.customized && (
          <Button
            size="small"
            disabled={saving}
            onClick={handleReset}
            sx={{ textTransform: "none", color: MUTED }}
          >
            Reset to original
          </Button>
        )}
        <Box flexGrow={1} />
        <Button
          disabled={!changed || saving}
          onClick={() => {
            setSubject(template.subject);
            setBody(savedBody);
          }}
          sx={{ textTransform: "none" }}
        >
          Undo changes
        </Button>
        <Button
          variant="contained"
          disableElevation
          disabled={!changed || saving || !subject.trim()}
          onClick={() =>
            run(
              saveEmailTemplate(template.kind, { subject, body }),
              "Changes saved"
            )
          }
          sx={{ textTransform: "none", px: 3 }}
        >
          {saving ? "Saving..." : "Save changes"}
        </Button>
      </Stack>

      <Menu
        anchorEl={fieldsAnchor}
        open={!!fieldsAnchor}
        onClose={() => setFieldsAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        PaperProps={{ sx: { maxHeight: 480 } }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {[
          [
            "This email",
            template.placeholders.filter(
              (f) => !ACCOUNT_FIELDS.includes(f.name)
            ),
          ],
          [
            "The account",
            template.placeholders.filter((f) =>
              ACCOUNT_FIELDS.includes(f.name)
            ),
          ],
        ]
          .filter(([, fields]) => fields.length)
          .flatMap(([title, fields]) => [
            <ListSubheader
              key={title}
              sx={{ lineHeight: "32px", fontSize: 12 }}
            >
              {title}
            </ListSubheader>,
            ...fields.map((f) => (
              <MenuItem key={f.name} onClick={() => insertField(f.name)}>
                <ListItemText
                  primary={f.description}
                  secondary={`{${f.name}}`}
                  primaryTypographyProps={{ fontSize: 14 }}
                  secondaryTypographyProps={{ fontSize: 12 }}
                />
              </MenuItem>
            )),
          ])}
      </Menu>

      <Snackbar
        open={!!toast}
        autoHideDuration={2500}
        onClose={() => setToast("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="success" variant="filled" onClose={() => setToast("")}>
          {toast}
        </Alert>
      </Snackbar>
    </Box>
  );
}

function HeaderLine({ label, htmlFor, children }) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={2}
      sx={{ px: 2.5, minHeight: 48, borderBottom: 1, borderColor: LINE }}
    >
      <Typography
        component={htmlFor ? "label" : "span"}
        htmlFor={htmlFor}
        sx={{ width: 64, flexShrink: 0, fontSize: 13, color: MUTED }}
      >
        {label}
      </Typography>
      {children}
    </Stack>
  );
}

// The email as a customer would see it, filled in with example values.
function EmailPreview({ kind, subject, body }) {
  const [preview, setPreview] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      previewEmailTemplate(kind, { subject, body })
        .then((res) => {
          setPreview(res.data);
          setFailed(false);
        })
        .catch(() => setFailed(true));
    }, 300);
    return () => clearTimeout(timer);
  }, [kind, subject, body]);

  if (failed) {
    return (
      <Alert severity="error" sx={{ borderRadius: 0 }}>
        Could not load the preview.
      </Alert>
    );
  }
  if (!preview) {
    return (
      <Box p={3}>
        <CircularProgress size={24} />
      </Box>
    );
  }
  return (
    <>
      <HeaderLine label="Subject">
        <Typography sx={{ fontSize: 15, fontWeight: 600 }}>
          {preview.subject}
        </Typography>
      </HeaderLine>
      {preview.unknown_placeholders.length > 0 && (
        <Alert severity="warning" sx={{ borderRadius: 0 }}>
          {preview.unknown_placeholders.map((p) => `{${p}}`).join(", ")}{" "}
          {preview.unknown_placeholders.length === 1
            ? "is not a field"
            : "are not fields"}{" "}
          this email can use. Remove it before saving.
        </Alert>
      )}
      <Typography sx={{ fontSize: 12, color: MUTED, px: 2.5, pt: 1.5 }}>
        Shown with example details for a made-up customer.
      </Typography>
      <Box
        component="iframe"
        title="Email preview"
        srcDoc={`<div style="font-family: Arial, Helvetica, sans-serif; font-size: 14px; color: #333; padding: 8px 20px 20px; max-width: 640px;">${preview.body}</div>`}
        sx={{ width: "100%", height: 440, border: 0, display: "block" }}
      />
    </>
  );
}
